import React, { forwardRef, useCallback, useRef } from 'react';
import Link from 'next/link';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useLenis } from 'lenis/react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { mergeRefs } from '@/utils/merge-refs.utils';

type Variant = 'fill' | 'transparent';

type CommonProps = {
  variant?: Variant;
  className?: string;
  children: React.ReactNode;
  isAnchor?: boolean;
  target?: React.AnchorHTMLAttributes<HTMLAnchorElement>['target'];
};

type ButtonAsButton = CommonProps &
  React.ButtonHTMLAttributes<HTMLButtonElement> & {
    href?: undefined;
    target?: undefined;
  };

type ButtonAsLink = CommonProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
    href: string;
    external?: boolean;
  };

export type ButtonProps = ButtonAsButton | ButtonAsLink;

const baseClasses =
  'inline-flex items-center justify-center rounded-full border-2 border-orange px-7.5 py-1.5 text-md focus:outline-none focus-visible:ring-2 focus-visible:ring-orange focus-visible:ring-offset-2 cursor-pointer overflow-hidden relative';

const variantClasses: Record<Variant, string> = {
  fill: 'bg-orange',
  transparent: 'bg-transparent',
};

function ButtonContent({ variant, children }: { variant: Variant; children: React.ReactNode }) {
  const isFill = variant === 'fill';
  return (
    <>
      <span
        className={clsx(
          'absolute inset-0 origin-bottom scale-y-0 rounded-full',
          isFill ? 'bg-white' : 'bg-orange',
        )}
        aria-hidden
        data-fill
      />
      <span className="relative z-10 flex flex-col overflow-hidden" style={{ height: '1.4em' }}>
        <span
          className={clsx(
            'block leading-[1.4em] whitespace-nowrap',
            isFill ? 'text-white' : 'text-orange',
          )}
          data-t1
        >
          {children}
        </span>
        <span
          className={clsx(
            'absolute top-full left-0 block leading-[1.4em] whitespace-nowrap',
            isFill ? 'text-orange' : 'text-white',
          )}
          aria-hidden
          data-t2
        >
          {children}
        </span>
      </span>
    </>
  );
}

function useFlipHover(ref: React.RefObject<HTMLElement | null>) {
  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;

      const tl = gsap
        .timeline({ paused: true })
        .to(el.querySelector('[data-fill]'), { scaleY: 1, duration: 0.45, ease: 'power3.inOut' }, 0)
        .to(
          el.querySelectorAll('[data-t1],[data-t2]'),
          { y: '-100%', duration: 0.45, ease: 'power3.inOut' },
          0,
        );

      el.addEventListener('mouseenter', () => tl.play());
      el.addEventListener('mouseleave', () => tl.reverse());
    },
    { scope: ref },
  );
}

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  (
    { variant = 'fill', className = '', children, href, target, isAnchor = false, ...rest },
    ref,
  ) => {
    const classes = clsx(baseClasses, variantClasses[variant], className);
    const isLink = typeof href === 'string';
    const isExternal = (isLink && /^https?:\/\//.test(href)) || target === '_blank';

    const router = useRouter();
    const lenis = useLenis();
    const innerRef = useRef<HTMLElement>(null);
    useFlipHover(innerRef);

    const handleAnchorClick = useCallback(
      (event: React.MouseEvent<HTMLAnchorElement>) => {
        if (!href) return;
        const url = new window.URL(href, window.location.origin);
        const hash = url.hash.replace('#', '');
        const path = url.pathname || '/';
        if (!hash) {
          if (url.origin === window.location.origin) {
            event.preventDefault();
            void router.push(url.pathname, undefined, { scroll: false });
          }
          return;
        }
        event.preventDefault();

        const scrollToHash = () => {
          const element = document.getElementById(hash);
          if (!element) return;
          const targetY = element.getBoundingClientRect().top + window.scrollY - 96;
          if (lenis) {
            lenis.scrollTo(targetY, { immediate: false });
          } else {
            window.scrollTo({ top: targetY, behavior: 'smooth' });
          }
        };

        if (router.pathname !== path) {
          router.push(href, undefined, { scroll: false });
        } else {
          scrollToHash();
        }
      },
      [href, lenis, router],
    );

    const content = <ButtonContent variant={variant}>{children}</ButtonContent>;

    if (isAnchor) {
      const { onClick, ...anchorRest } = rest as React.AnchorHTMLAttributes<HTMLAnchorElement>;
      return (
        <a
          ref={ref as React.Ref<HTMLAnchorElement>}
          className={className}
          href={href!}
          target={target}
          {...anchorRest}
          onClick={(e) => {
            onClick?.(e);
            if (!e.defaultPrevented) handleAnchorClick(e);
          }}
        >
          {children}
        </a>
      );
    }

    if (isLink) {
      if (isExternal || target === '_blank') {
        return (
          <a
            ref={mergeRefs(innerRef, ref) as React.Ref<HTMLAnchorElement>}
            className={classes}
            href={href}
            rel={target === '_blank' ? 'noreferrer' : undefined}
            target={target}
            {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
          >
            {content}
          </a>
        );
      }
      return (
        <Link
          ref={mergeRefs(innerRef, ref) as React.Ref<HTMLAnchorElement>}
          className={classes}
          href={href}
          scroll={false}
          {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {content}
        </Link>
      );
    }

    return (
      <button
        ref={mergeRefs(innerRef, ref) as React.Ref<HTMLButtonElement>}
        className={classes}
        {...(rest as React.ButtonHTMLAttributes<HTMLButtonElement>)}
      >
        {content}
      </button>
    );
  },
);

Button.displayName = 'Button';
